import {
  type ProjectDefaultViewEnum,
  type ProjectStatusEnum,
  type TaskDependenciesEnum,
  type TaskStatusEnum,
  type OrgStatusEnum,
  type ProjectOverAllTrackEnum,
  type UserStatusEnum,
  type ConsoleStatusEnum,
  type HistoryTypeEnum,
  type ScheduleAndBudgetTrendEnum,
  type LanguageEnum,
  type PlanType,
  type BillingCycle,
  type ImpactEnum,
  type ProbabilityEnum,
  type SeverityEnum,
  type StatusEnum,
  type TrendEnum,
  ProgramRoleEnum,
  PortfolioRoleEnum,
  UserRoleEnum,
  TimesheetProjectImpactEnum,
  type DeliverableStatusEnum,
  type DeliverableListTypeEnum,
  type DeliverableDocTypeEnum,
  type DeliverableVersionStatusEnum,
  type DeliverableHistoryEventEnum,
} from "@prisma/client";
import type { EnumStringValueObj } from "../types/enumStringValueObject.js";

export const ProjectDefaultViewEnumValue: EnumStringValueObj<ProjectDefaultViewEnum> =
  {
    CALENDAR: "CALENDAR",
    GANTT: "GANTT",
    KANBAN: "KANBAN",
    LIST: "LIST",
  };

export const ProjectStatusEnumValue: EnumStringValueObj<ProjectStatusEnum> = {
  ACTIVE: "ACTIVE",
  CLOSED: "CLOSED",
  NOT_STARTED: "NOT_STARTED",
  ON_HOLD: "ON_HOLD",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
};

/**
 * Maps legacy DB statuses to the current display statuses.
 * ACTIVE  → IN_PROGRESS
 * CLOSED  → COMPLETED
 * All other values pass through unchanged.
 */
export function normalizeProjectStatus(status: string): string {
  if (status === "ACTIVE") return "IN_PROGRESS";
  if (status === "CLOSED") return "COMPLETED";
  return status;
}

/** The set of statuses that mean "project is actively worked on" (legacy + current). */
export const ACTIVE_PROJECT_STATUSES: ProjectStatusEnum[] = [
  "ACTIVE" as ProjectStatusEnum,
  "IN_PROGRESS" as ProjectStatusEnum,
];

/** The set of statuses that mean "project is closed/done" (legacy + current). */
export const CLOSED_PROJECT_STATUSES: ProjectStatusEnum[] = [
  "CLOSED" as ProjectStatusEnum,
  "COMPLETED" as ProjectStatusEnum,
];
export const ProjectBudgetEnumValue = {
  PROJECT_IS_SIGNIFICANTLY_OVER_BUDGET: "PROJECT_IS_SIGNIFICANTLY_OVER_BUDGET",
  PROJECT_IS_MODERATELY_OVER_BUDGET: "PROJECT_IS_MODERATELY_OVER_BUDGET",
  PROJECT_IS_ON_BUDGET: "PROJECT_IS_ON_BUDGET",
  PROJECT_IS_UNDER_BUDGET: "PROJECT_IS_UNDER_BUDGET",
};


export const TaskDependenciesEnumValue: EnumStringValueObj<TaskDependenciesEnum> =
  {
    PREDECESSORS: "PREDECESSORS",
    SUCCESSORS: "SUCCESSORS",
  };

export const TaskStatusEnumValue: EnumStringValueObj<TaskStatusEnum> = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
};

export const OrgStatusEnumValue: EnumStringValueObj<OrgStatusEnum> = {
  ACTIVE: "ACTIVE",
  DEACTIVE: "DEACTIVE",
};

export const UserStatusEnumValue: EnumStringValueObj<UserStatusEnum> = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
};

export const OperatorStatusEnumValue: EnumStringValueObj<ConsoleStatusEnum> = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
};

export const UserRoleEnumValue: EnumStringValueObj<UserRoleEnum> = {
  ADMINISTRATOR: "ADMINISTRATOR",
  PROJECT_MANAGER: "PROJECT_MANAGER",
  TEAM_MEMBER: "TEAM_MEMBER",
  COLLABORATOR: "COLLABORATOR",
};

export const PortfolioRoleEnumValue: EnumStringValueObj<PortfolioRoleEnum> = {
  PORTFOLIO_MANAGER: "PORTFOLIO_MANAGER",
};

export const ProgramRoleEnumValue: EnumStringValueObj<ProgramRoleEnum> = {
  PROGRAM_MANAGER: "PROGRAM_MANAGER",
};

export const ZodErrorMessageEnumValue = {
  REQUIRED: "Required*",
  PORTFOLIO_OR_PROGRAM_REQUIRED: "portfolio_or_program_required",
};

export const HistoryTypeEnumValue: EnumStringValueObj<HistoryTypeEnum> = {
  PROJECT: "PROJECT",
  TASK: "TASK",
  RISK: "RISK"
};

export const OverAllTrackEnumValue: EnumStringValueObj<ProjectOverAllTrackEnum> =
  {
    CLOUDY: "CLOUDY",
    RAINY: "RAINY",
    STORMY: "STORMY",
    SUNNY: "SUNNY",
  };

export const ScheduleAndBudgetTrend: EnumStringValueObj<ScheduleAndBudgetTrendEnum> =
  {
    STABLE: "STABLE",
    INCREASING: "INCREASING",
    DECREASING: "DECREASING",
  };

export const LanguageEnumSchema: EnumStringValueObj<LanguageEnum> = {
  EN: "EN",
  FR: "FR",
};
export const PlanTypeEnumSchema: EnumStringValueObj<PlanType> = {
  ENTERPRISE: "ENTERPRISE",
  BUSINESS: "BUSINESS",
  FREE: "FREE",
};

export const ExtendPlanTypeEnumSchema = {
  ...PlanTypeEnumSchema,
  FREE_TRIAL: "FREE_TRIAL",
} as const;

export const DefaultAbsentTypeEnum = {
  DEFAULT: "DEFAULT",
} as const;

export const BillingCycleSchema: EnumStringValueObj<BillingCycle> = {
  MONTH: "MONTH",
  YEAR: "YEAR",
};

export const ImpactEnumSchema: EnumStringValueObj<ImpactEnum> = {
  MINOR: "MINOR",
  MAJOR: "MAJOR",
  SERIOUS: "SERIOUS",
  CATASTROPHIC: "CATASTROPHIC",
};
export const ProbabilityEnumSchema: EnumStringValueObj<ProbabilityEnum> = {
  IMPROBABLE: "IMPROBABLE",
  UNLIKELY: "UNLIKELY",
  LIKELY: "LIKELY",
  VERY_LIKELY: "VERY_LIKELY",
};
export const SeverityEnumSchema: EnumStringValueObj<SeverityEnum> = {
  VERY_CRITICAL: "VERY_CRITICAL",
  CRITICAL: "CRITICAL",
  MODERATE: "MODERATE",
};
export const StatusEnumSchema: EnumStringValueObj<StatusEnum> = {
  ACTIVE: "ACTIVE",
  CLOSE: "CLOSE",
};
export const TrendEnumSchema: EnumStringValueObj<TrendEnum> = {
  STABLE: "STABLE",
  INCREASING: "INCREASING",
  DECREASING: "DECREASING",
};

export const TimesheetProjectImpactEnumValue: EnumStringValueObj<TimesheetProjectImpactEnum> = {
  EXCLUDED: "EXCLUDED",
  INCLUDED: "INCLUDED",
  OPTIONAL: "OPTIONAL",
};

export const DeliverableStatusEnumValue: EnumStringValueObj<DeliverableStatusEnum> = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  DELIVERED: "DELIVERED",
  APPROVED: "APPROVED",
  REWORK_REQUIRED: "REWORK_REQUIRED",
  CANCELLED: "CANCELLED",
};

export const DeliverableListTypeEnumValue: EnumStringValueObj<DeliverableListTypeEnum> = {
  DESTINATION: "DESTINATION",
  TYPE: "TYPE",
};

export const DeliverableDocTypeEnumValue: EnumStringValueObj<DeliverableDocTypeEnum> = {
  FILE: "FILE",
  LINK: "LINK",
  NONE: "NONE",
};

export const DeliverableVersionStatusEnumValue: EnumStringValueObj<DeliverableVersionStatusEnum> = {
  DRAFT: "DRAFT",
  DELIVERED: "DELIVERED",
  CHANGES_REQUESTED: "CHANGES_REQUESTED",
  APPROVED: "APPROVED",
};

export const DeliverableHistoryEventEnumValue: EnumStringValueObj<DeliverableHistoryEventEnum> = {
  DELIVERY: "DELIVERY",
  APPROVAL: "APPROVAL",
  CHANGE_REQUEST: "CHANGE_REQUEST",
  STATUS_CHANGE: "STATUS_CHANGE",
  COMMENT: "COMMENT",
};
